import React from "react";
import { Helmet } from "react-helmet";
import {
  Box,
  Paper,
  Container,
  Card,
  Typography,
  TableCell,
  Button,
  Popper,
  MenuList,
  MenuItem, ClickAwayListener,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import MoreHorizIcon from "@material-ui/icons/MoreHoriz";
import DataTable from "../../../../components/DataTable";
import { useAdminContext } from "../../contexts/admin.context";

const style = (theme) => ({
  paper: {
    backgroundColor: "#3A4D54",
    color: theme.palette.common.white,
    borderRadius: "4px",
  },
  boxSubmenu: {
    "&::before": {
      content: '""',
      position: "absolute",
      width: "0",
      height: "0",
      top: "8px",
      right: "-15px",
      border: "8px solid",
      borderColor: "transparent transparent transparent",
      borderLeftColor: "#3A4D54",
      zIndex: 1,
    },
  },
  divider: {
    height: "70%",
    backgroundColor: theme.palette.divider,
    margin: theme.spacing(0, 1),
  },
  menuItem: {
    fontSize: "small",
    display: "block",
  },
});

const useStyles = makeStyles(style);

const statuses = {
  active: {
    label: "Active",
    color: "success.main",
  },
  suspend: {
    label: "Suspended",
    color: "error.main",
  },
  pending: {
    label: "Pending",
    color: "warning.main",
  },
};

export default function AdminsPage({
  isCompaniesLoading,
  admins,
  totalCompanies,
  companiesPage,
  itemPerPage,
  allBranchWhiteLabelCount,
  anchorEl,
  anchorRef,
  handleActionOpen,
  handleAdminRoleOpen,
  handleGroupClick,
  isLoading,
  page,
  handlePageChange,
  handleItemPerPageChange,
  total,
  onCreateClick,
  onEditClick,
  onStatusChangeClick,
  canEdit,
  handleClose,
}) {
  const { selectedAdmin, setSelectedAdmin, actionOpen, setActionOpen } = useAdminContext();
  const classes = useStyles();
  return (
    <>
      <Helmet>
        <title>Admins | Kood Asia Management Portal</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: "background.default",
          minHeight: "100%",
          paddingTop: 3,
          paddingBottom: 12,
        }}
      >
        <Container maxWidth={false}>
          <Card sx={{ mb: 2 }}>
            <Box display="flex" flexDirection="row" alignItems="center" justifyContent="space-between">
              <Typography
                variant="h4"
                sx={{
                  padding: 2,
                }}
              >
                Admins
              </Typography>
              {canEdit && (
                <Box mx={2}>
                  <Button onClick={onCreateClick}>
                    <Typography variant="button">Add Admin +</Typography>
                  </Button>
                </Box>
              )}
            </Box>
          </Card>

          <DataTable
            isLoading={isLoading || isCompaniesLoading}
            rows={admins}
            rowsPerPage={itemPerPage}
            total={total}
            page={page}
            renderHeader={() => (
              <>
                <TableCell>Email</TableCell>
                <TableCell>Username</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Company Group Assigned</TableCell>
                <TableCell>Status</TableCell>
                {canEdit && (
                  <TableCell align="right">
                    <Box mx={2}>
                      Action
                    </Box>
                  </TableCell>
                )}
              </>
            )}
            renderRow={(row, index) => (
              <>
                <TableCell>{row.email}</TableCell>
                <TableCell>{row.username}</TableCell>
                <TableCell>{row.role_display_name}</TableCell>
                <TableCell>{row.group_name ?? '-'}</TableCell>
                <TableCell>
                  <Typography color={statuses[row.state].color}>
                    {statuses[row.state].label ?? row.state}
                  </Typography>
                </TableCell>
                {/* Action button starts here */}
                {canEdit && row.email !== 'admin@foqcus.com' && (
                  <TableCell align="right">
                    <Button
                      onClick={(event) => {
                        handleActionOpen(event);
                        setSelectedAdmin(row);
                      }}
                    >
                      <MoreHorizIcon />
                    </Button>
                    <Popper
                      open={actionOpen && selectedAdmin.id === row.id}
                      anchorEl={anchorEl}
                      onClose={() => setActionOpen(false)}
                      placement="left-start"
                      style={{ zIndex: 1 }}
                    >
                      <Box className={classes.boxSubmenu}>
                        <Paper className={classes.paper}>
                          <ClickAwayListener onClickAway={handleClose}>
                            <MenuList>
                              {[
                                {
                                  label: "Edit",
                                  enabled: row.role_display_name !== 'Master',
                                  onClick: () => onEditClick(row),
                                },
                                {
                                  label: row.state === 'active' ? 'Deactivate' : 'Activate',
                                  enabled: row.role_display_name !== 'Master',
                                  destructive: row.state !== 'active',
                                  onClick: () => onStatusChangeClick(row),
                                },
                                {
                                  label: "Change Admin Role",
                                  enabled: row.role_display_name !== 'Master',
                                  onClick: () => handleAdminRoleOpen()
                                },
                                {
                                  label: "Change Company Group",
                                  enabled: row.role_display_name !== 'Master',
                                  onClick: () => handleGroupClick(row)
                                }
                              ].map((item) =>
                                (
                                  <MenuItem
                                    className={classes.menuItem}
                                    key={row}
                                    onClick={(e) => {
                                      item.onClick();
                                    }}
                                  >
                                    <span style={{ color: item.destructive ? 'red' : 'white' }}>{item.label}</span>
                                  </MenuItem>
                                )
                              )}
                            </MenuList>
                          </ClickAwayListener>
                        </Paper>
                      </Box>
                    </Popper>
                  </TableCell>
                )}
              </>
            )}
            onPageClick={handlePageChange}
            handleItemPerPageChange={handleItemPerPageChange}
          />
        </Container>
      </Box>
    </>
  );
}
